import React, {useState, useEffect} from 'react';
import {
    Collapse,
    Divider,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    styled,
    Toolbar,
    Drawer as MuiDrawer,
    Typography,
    Box,
    Menu,
    MenuItem
} from "@mui/material";
import {useLocation} from 'react-router';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faMobile,
    faChartTreeMap,
    faTimelineArrow,
    faChartGantt,
    faListCheck,
    faUsers,
    faQrcode,
    faKey,
    faFileCertificate,
    faArrowUp,
    faFileCircleQuestion,
    faGavel,
    faCaretDown,
    faCaretUp,
    faChevronLeft,
    faGrid2,
    faGears,
    faHeadset,
    faClipboardList,
    faFileCode,
    faMagnifyingGlass,
    faPhoneVolume,
    faKeySkeleton,
    faMap,
    faShieldHalved,
    faUserPlus,
    faCalculator,
    faWarehouse,
    faCalendarDays
} from '@fortawesome/pro-solid-svg-icons'
import {EUserGroup, GetProductsData} from 'apiClient/Api';
import {RouterLink} from "Components/RouterLink"
import {useTranslation} from 'react-i18next';
import {useApplicationContext} from "ApplicationContext";
import { getDrawerWidth } from '../config/drawerConfig';
import { SupportIcon } from '../SupportIcon';
const Drawer = styled(MuiDrawer, {shouldForwardProp: (prop) => prop !== 'open'})(
    ({theme, open}) => {
        const { i18n } = useTranslation();
        const drawerWidth = getDrawerWidth(i18n.language);

        return {
            '& .MuiDrawer-paper': {
                position: 'relative',
                whiteSpace: 'nowrap',
                width: drawerWidth,
                transition: theme.transitions.create(['width'], {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                }),
                boxSizing: 'border-box',
                ...(!open && {
                    overflowX: 'hidden',
                    transition: theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                    width: theme.spacing(7),
                }),
            },
        }
    },
);

const TemporaryDrawer = styled(MuiDrawer)(
    () => {
        const { i18n } = useTranslation();
        const drawerWidth = getDrawerWidth(i18n.language);

        return {
            '& .MuiDrawer-paper': {
                width: drawerWidth,
                boxSizing: 'border-box',
            },
        }
    },
);

interface MyDrawerProps {
    open: boolean;
    handleDrawerToggle: () => void;
    isMobile: boolean;
}

interface NavItem {
    to?: string;
    textKey?: string;
    icon?: any;
    minUserGroup?: EUserGroup;
    maxUserGroup?: EUserGroup;
    mobileOnly?: boolean;
    desktopOnly?: boolean;
    isDivider?: boolean;
    isCollapsible?: boolean;
    section?: string;
    linkPrefix?: string;
    showInMini?: boolean; // Für mobile Mini-Drawer
    requireCalculatorOperate?: boolean; // sichtbar wenn canOperateCalculator (Rolle ODER calculator.operate)
    hideIfCalculatorOperate?: boolean; // ausblenden für Bediener (die nutzen den vollen Kalkulator)
    permission?: string; // nur sichtbar mit dieser Permission (Admins über die Admin-Brücke immer)
}

/**
 * CollapsibleSection component to handle sections with collapsible lists.
 * @param props The properties for the component.
 */
const CollapsibleSection: React.FC<{
    section: string;
    icon: any;
    title: string;
    products: GetProductsData[];
    linkPrefix: string;
}> = ({section, icon, title, products, linkPrefix}) => {
    const {t} = useTranslation();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(location.pathname.startsWith(linkPrefix));

    /**
     * Toggles the open state of the section.
     */
    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    if (products.length === 1) {
        return (
            <RouterLink to={`${linkPrefix}/${products[0].link!}`}
                        text={t(title)}
                        icon={icon}/>
        );
    }

    return (
        <>
            <ListItemButton onClick={handleToggle}>
                <ListItemIcon>
                    {icon}
                </ListItemIcon>
                <ListItemText primary={t(title)}/>
                {isOpen ? <FontAwesomeIcon icon={faCaretUp}/> : <FontAwesomeIcon icon={faCaretDown}/>}
            </ListItemButton>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                    {products.map(product =>
                        <RouterLink to={`${linkPrefix}/${product.link!}`}
                                    text={product.name ?? ""}
                                    icon={icon}/>)}
                </List>
            </Collapse>
        </>
    );
};

const SupportDrawer: React.FC<MyDrawerProps> = ({open, handleDrawerToggle, isMobile}) => {
    const context = useApplicationContext();
    const [products, setProducts] = useState<GetProductsData[]>([]);
    const { i18n } = useTranslation();
    const drawerWidth = getDrawerWidth(i18n.language);

    useEffect(() => {
        context.api.products.productsGetProducts().then(data => {
            if (data && data.data)
                setProducts(data.data)
        })
    }, []);

    const {t} = useTranslation();

    /**
     * Central navigation items configuration with useMemo
     */
    const navItems = React.useMemo((): NavItem[] => [
        { to: "/", textKey: "base.pages.dashboard", icon: faChartTreeMap, showInMini: true },
        { isCollapsible: true, section: "versions", icon: faTimelineArrow, textKey: "base.pages.versions", linkPrefix: "/versions", showInMini: true },
        { isCollapsible: true, section: "applications", icon: faGrid2, textKey: "base.pages.applications", linkPrefix: "/applications", showInMini: true },
        { to: "/support-contracts", textKey: "base.pages.supportContracts", icon: faQrcode, showInMini: true },
        { to: "/deviceManagement", textKey: "deviceManagement.title", icon: faMobile, showInMini: true },
        { to: "/fileshare", textKey: "base.pages.fileshare", icon: faFileCircleQuestion, showInMini: true },
        { to: "/licenses", textKey: "base.pages.licenses", icon: faFileCertificate, showInMini: true },
        { to: "/tickets", textKey: "base.pages.tickets", icon: faHeadset, showInMini: true },
        { to: "/security", textKey: "base.pages.security", icon: faShieldHalved, showInMini: true },
        { to: "/terms", textKey: "base.pages.terms", icon: faGavel, minUserGroup: EUserGroup.ADMIN },
        { to: "/api-keys", textKey: "apiKeys.menuItem", icon: faKeySkeleton, maxUserGroup: EUserGroup.OFFICE, showInMini: true },
        { isDivider: true, maxUserGroup: EUserGroup.OFFICE },
        { to: "/kalkulation", textKey: "base.pages.calculation", icon: faCalculator, requireCalculatorOperate: true, showInMini: true },
        { to: "/kalkulation/meine", textKey: "base.pages.myCalculations", icon: faCalculator, hideIfCalculatorOperate: true, showInMini: true },
        { to: "/kalkulation/stammdaten", textKey: "base.pages.masterData", icon: faWarehouse, maxUserGroup: EUserGroup.OFFICE, showInMini: false },
        { to: "/kalender", textKey: "calendar.menuItem", icon: faCalendarDays, maxUserGroup: EUserGroup.OFFICE, showInMini: true },
        { to: "/aufgaben", textKey: "labelTasks.menuItem", icon: faListCheck, maxUserGroup: EUserGroup.OFFICE, showInMini: true },
        { to: "/planer", textKey: "planner.menuItem", icon: faChartGantt, maxUserGroup: EUserGroup.OFFICE, permission: 'planner.access', showInMini: true },
        { to: "/service-search", textKey: "unifiedSearch.menuItem", icon: faMagnifyingGlass, maxUserGroup: EUserGroup.OFFICE, showInMini: true },
        { to: "/rueckrufe", textKey: "callbacks.menuItem", icon: faPhoneVolume, maxUserGroup: EUserGroup.OFFICE, showInMini: true },
        { to: "/admin/product-examples", textKey: "productExamples.menuItem", icon: faFileCode, maxUserGroup: EUserGroup.OFFICE, showInMini: true },
        { to: "/admin/users", textKey: "base.pages.users", icon: faUsers, maxUserGroup: EUserGroup.OFFICE, desktopOnly: true, showInMini: true },
        { to: "/admin/registrations", textKey: "base.pages.registrations", icon: faUserPlus, maxUserGroup: EUserGroup.OFFICE, desktopOnly: true, showInMini: true },
        { to: "/admin/support-contracts", textKey: "base.pages.supportContracts", icon: faQrcode, maxUserGroup: EUserGroup.OFFICE, desktopOnly: true },
        { to: "/admin/coredumps", textKey: "base.pages.coreDumps", icon: faQrcode, maxUserGroup: EUserGroup.OFFICE, desktopOnly: true },
        { to: "/admin/license_requests", textKey: "base.pages.licenseRequests", icon: faFileCertificate, maxUserGroup: EUserGroup.OFFICE, showInMini: false },
        { to: "/licenses/requested", textKey: "base.pages.requestedLicenses", icon: faFileCertificate, maxUserGroup: EUserGroup.OFFICE, showInMini: false },
        { to: "/admin/password_generator", textKey: "base.pages.passwordGenerator", icon: faKey, maxUserGroup: EUserGroup.DEVELOPMENT },
        { to: "/admin/license_generator", textKey: "base.pages.licenseGenerator", icon: faFileCertificate, maxUserGroup: EUserGroup.DEVELOPMENT },
        { isCollapsible: true, section: "adminVersions", icon: faArrowUp, textKey: "base.pages.versions", linkPrefix: "/admin/versions", maxUserGroup: EUserGroup.ADMIN, showInMini: false },
        // Verwaltung gebündelt: AD-Anbindung, Berechtigungen, Standort-Dienst, Dateitypen,
        // Systeme, Filter-Tags und Bedingungen liegen als Unterseiten unter /admin/settings.
        // Werkzeuge (Audit-Log, Geodaten-Konverter) bleiben bewusst eigene Einträge — das sind
        // keine Einstellungen.
        { to: "/admin/settings", textKey: "settings.title", icon: faGears, maxUserGroup: EUserGroup.ADMIN, desktopOnly: true },
        { to: "/admin/audit-log", textKey: "base.pages.auditLog", icon: faClipboardList, maxUserGroup: EUserGroup.ADMIN, desktopOnly: true },
        { to: "/admin/geodata-converter", textKey: "base.pages.geodataConverter", icon: faMap, maxUserGroup: EUserGroup.ADMIN, desktopOnly: true },
    ], []);

    /**
     * Mini icon item component with optional menu for collapsible sections
     */
    const MiniIconItem: React.FC<{ item: NavItem, index: number }> = ({ item, index }) => {
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
        const menuOpen = Boolean(anchorEl);

        const handleClick = (event: React.MouseEvent<HTMLElement>) => {
            if (item.isCollapsible && products.length > 1) {
                setAnchorEl(event.currentTarget);
            } else if (item.isCollapsible && products.length === 1) {
                window.location.href = `${item.linkPrefix}/${products[0].link}`;
            } else {
                window.location.href = item.to!;
            }
        };

        const handleClose = () => {
            setAnchorEl(null);
        };

        const handleMenuItemClick = (productLink: string) => {
            window.location.href = `${item.linkPrefix}/${productLink}`;
            handleClose();
        };

        return (
            <>
                <ListItemButton 
                    onClick={handleClick}
                    sx={{ justifyContent: 'center', px: 1, py: 1.5 }}
                >
                    <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center', fontSize: '1.5rem' }}>
                        <FontAwesomeIcon icon={item.icon} />
                    </ListItemIcon>
                </ListItemButton>
                
                {item.isCollapsible && products.length > 1 && (
                    <Menu
                        anchorEl={anchorEl}
                        open={menuOpen}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                    >
                        {products.map((product) => (
                            <MenuItem 
                                key={product.link} 
                                onClick={() => handleMenuItemClick(product.link!)}
                            >
                                <ListItemIcon>
                                    <FontAwesomeIcon icon={item.icon} />
                                </ListItemIcon>
                                <ListItemText>{product.name}</ListItemText>
                            </MenuItem>
                        ))}
                    </Menu>
                )}
            </>
        );
    };

    /**
     * Filters navigation items based on user permissions and context
     */
    const filterNavItems = (item: NavItem, isMini: boolean = false): boolean => {
        if (!context.userData) return false;
        
        // For mini drawer, only show items with showInMini flag
        if (isMini && !item.showInMini) return false;
        
        // Skip dividers in mini drawer but allow collapsible sections
        if (isMini && item.isDivider) return false;
        
        // Check mobile/desktop specific items
        if (isMini && item.desktopOnly) return false;
        if (!isMini && item.mobileOnly) return false;
        
        // Check user group permissions
        const userGroup = context.userData.userGroup!;
        if (item.minUserGroup !== undefined && userGroup < item.minUserGroup) return false;
        if (item.maxUserGroup !== undefined && userGroup > item.maxUserGroup) return false;

        // Feingranulare Permission (Admins besitzen über die Admin-Brücke alle).
        if (item.permission !== undefined && !context.hasPermission(item.permission)) return false;

        // Kalkulator: sichtbar für OFFICE+ ODER Partner mit calculator.operate.
        if (item.requireCalculatorOperate && !context.canOperateCalculator) return false;
        // „Meine Kalkulationen": nur für Nicht-Bediener (Bediener haben den vollen Kalkulator).
        if (item.hideIfCalculatorOperate && context.canOperateCalculator) return false;

        return true;
    };

    const drawerContent = (
        <>
            <Toolbar
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    px: [1],
                }}
            >
                <Box sx={{flexGrow: 1, display: 'flex', alignItems: 'center'}}>
                    <SupportIcon size={60} />
                    <Typography variant="h6" sx={{ml: 2}} color={"primary"}>
                        System
                    </Typography>
                </Box>
                <IconButton onClick={handleDrawerToggle}>
                    <FontAwesomeIcon icon={faChevronLeft}/>
                </IconButton>
            </Toolbar>
            <Divider/>
            {context.userData &&
                <List component="nav">
                    {navItems.filter(item => filterNavItems(item, false)).map((item, index) => {
                        if (item.isDivider) {
                            return <Divider key={`divider-${index}`} sx={{ my: 1 }} />;
                        }
                        if (item.isCollapsible) {
                            return (
                                <CollapsibleSection
                                    key={item.section}
                                    section={item.section!}
                                    icon={<FontAwesomeIcon icon={item.icon} />}
                                    title={item.textKey!}
                                    products={products}
                                    linkPrefix={item.linkPrefix!}
                                />
                            );
                        }
                        return (
                            <RouterLink 
                                key={item.to}
                                to={item.to!}
                                text={t(item.textKey!)}
                                icon={<FontAwesomeIcon icon={item.icon} />}
                            />
                        );
                    })}
                </List>
            }
        </>
    );

    if (isMobile) {
        return (
            <>
                {/* Permanent mini drawer for mobile */}
                <Drawer variant="permanent" open={false}>
                    <Toolbar
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: [1],
                        }}
                    >
                        <IconButton onClick={handleDrawerToggle}>
                            <SupportIcon size={40} />
                        </IconButton>
                    </Toolbar>
                    <Divider/>
                    {context.userData && (
                        <List component="nav" sx={{ py: 0 }}>
                            {navItems.filter(item => filterNavItems(item, true)).map((item, index) => (
                                <MiniIconItem key={item.to || item.section || index} item={item} index={index} />
                            ))}
                        </List>
                    )}
                </Drawer>

                {/* Temporary overlay drawer for mobile */}
                <TemporaryDrawer
                    variant="temporary"
                    open={open}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true,
                    }}
                >
                    {drawerContent}
                </TemporaryDrawer>
            </>
        );
    }

    // Desktop
    return (
        <Drawer variant="permanent" open={open} sx={{width: drawerWidth}}>
            {drawerContent}
        </Drawer>
    );
}

export default SupportDrawer;
